import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  InputSearch,
  TableData,
  Pagination,
  showErrorToast,
} from '@/components';
import { Column } from '@/types/common';
import {
  getDeliveries,
  getDeliverySummary,
  downloadDeliveryReconciliation,
  Delivery,
  DeliverySummary,
} from '@/services/api/deliveries';
import { DeliveryStatusBadge } from './DeliveryStatusBadge';
import DeliveryFilter, {
  DeliveryFilterValue,
  EMPTY_FILTER,
} from './DeliveryFilter';
import DeliveryDetailDrawer from './DeliveryDetailDrawer';

// ─── Status-tile groups ───────────────────────────────────────────────
// Each tile filters the list by a group of API statuses and shows a count
// from the summaries endpoint. `null` (no tile) = the full status list.
type TileKey = 'pending' | 'inDelivery' | 'completed' | 'failed' | 'cancelled';

interface TileDef {
  key: TileKey;
  label: string;
  dotClass: string;
  statuses: string[];
  count: (s: DeliverySummary) => number;
}

const TILES: TileDef[] = [
  {
    key: 'pending',
    label: 'Pending Delivery',
    dotClass: 'bg-feature-base',
    statuses: ['PENDING_DELIVERY'],
    count: (s) => (s.allocating ?? 0) + (s.pickingUp ?? 0),
  },
  {
    key: 'inDelivery',
    label: 'In Delivery',
    dotClass: 'bg-information-base',
    statuses: ['IN_DELIVERY'],
    count: (s) => s.inDelivery ?? 0,
  },
  {
    key: 'completed',
    label: 'Completed',
    dotClass: 'bg-success-base',
    statuses: ['COMPLETED'],
    count: (s) => s.completed ?? 0,
  },
  {
    key: 'failed',
    label: 'Failed',
    dotClass: 'bg-error-base',
    statuses: ['FAILED', 'FAILED_IN_RETURN'],
    count: (s) => s.failed ?? 0,
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    dotClass: 'bg-faded-base',
    statuses: ['CANCELLED'],
    count: (s) => s.cancelled ?? 0,
  },
];

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : '—';

const partyName = (party?: Delivery['recipient']) => {
  if (!party) return '—';
  if (party.companyName) return party.companyName;
  const name = `${party.firstName ?? ''} ${party.lastName ?? ''}`.trim();
  return name || '—';
};

const slaBadge = (status?: 'ON_TIME' | 'LATE' | null) => {
  if (status === 'ON_TIME')
    return (
      <span className="inline-flex items-center rounded-full bg-success-lighter px-2.5 py-0.5 text-xs font-medium text-success-dark">
        On time
      </span>
    );
  if (status === 'LATE')
    return (
      <span className="inline-flex items-center rounded-full bg-error-lighter px-2.5 py-0.5 text-xs font-medium text-error-dark">
        Late
      </span>
    );
  return <span className="text-text-soft-400">—</span>;
};

const DeliveriesPage: React.FC = () => {
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<DeliverySummary>({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [activeTile, setActiveTile] = useState<TileKey | null>(null);
  const [filters, setFilters] = useState<DeliveryFilterValue>(EMPTY_FILTER);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const statusParam = useMemo(() => {
    // Explicit status filter wins; otherwise fall back to the active tile's
    // status group; otherwise undefined (API returns the full status list).
    if (filters.statuses.length) return filters.statuses.join(',');
    if (!activeTile) return undefined;
    return TILES.find((t) => t.key === activeTile)?.statuses.join(',');
  }, [filters.statuses, activeTile]);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDeliveries({
        page,
        size: pageSize,
        status: statusParam,
        search,
        startDate: filters.startDate,
        endDate: filters.endDate,
        providerOutletIDs: filters.outlets.map((o) => o.value).join(','),
        driverIDs: filters.drivers.map((o) => o.value).join(','),
      });
      setDeliveries(res.data ?? []);
      setTotal(res.pagination?.total ?? 0);
    } catch (error) {
      showErrorToast(
        'Gagal memuat data',
        error instanceof Error ? error.message : 'Terjadi kesalahan.'
      );
      setDeliveries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusParam, search, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await getDeliverySummary({
        startDate: filters.startDate,
        endDate: filters.endDate,
        providerOutletIDs: filters.outlets.map((o) => o.value).join(','),
      });
      setSummary(data ?? {});
    } catch {
      // Summary is non-critical — leave tiles at 0 if it fails.
      setSummary({});
    }
  }, [filters.startDate, filters.endDate, filters.outlets]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleRefresh = () => {
    fetchDeliveries();
    fetchSummary();
  };

  // ─── Downloads ──────────────────────────────────────────────────────
  // Reconciliation CSV for the current filter set (page-level Download).
  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      const outletIDs = filters.outlets.map((o) => o.value).join(',');
      await downloadDeliveryReconciliation({
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: statusParam,
        providerOutletIDs: outletIDs,
        showOutlets: outletIDs.length > 0,
      });
    } catch (error) {
      showErrorToast(
        'Gagal mengunduh',
        error instanceof Error ? error.message : 'Terjadi kesalahan.'
      );
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleTileClick = (key: TileKey) => {
    setActiveTile((prev) => (prev === key ? null : key));
    // A tile drives status; drop any explicit status filter so they don't clash.
    setFilters((f) => (f.statuses.length ? { ...f, statuses: [] } : f));
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleApplyFilters = (next: DeliveryFilterValue) => {
    setFilters(next);
    // An explicit status filter takes over from the tile selection.
    if (next.statuses.length) setActiveTile(null);
    setPage(1);
  };

  const columns: Column<Delivery>[] = [
    {
      key: 'deliveryID',
      title: 'Delivery ID',
      sticky: 'left',
      render: (_v, record) => (
        <span className="font-mono text-xs font-semibold text-primary">
          {record.deliveryID}
        </span>
      ),
    },
    {
      key: 'providerOrderID',
      title: 'Booking ID',
      render: (_v, record) => (
        <span className="font-mono text-xs text-text-sub-600">
          {record.providerOrderID || '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      render: (_v, record) => (
        <span className="text-text-sub-600">
          {formatDateTime(record.createdAt)}
        </span>
      ),
    },
    {
      key: 'courier',
      title: 'Rider',
      render: (_v, record) => (
        <span className="text-text-strong-950">
          {record.courier?.name || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, record) => <DeliveryStatusBadge status={record.status} />,
    },
    {
      key: 'schedule',
      title: 'Schedule',
      render: (_v, record) => (
        <span className="text-text-sub-600">
          {formatDateTime(record.schedule?.pickupTimeFrom)}
        </span>
      ),
    },
    {
      key: 'recipient',
      title: 'Recipient',
      render: (_v, record) => (
        <span className="font-medium text-text-strong-950 text-xs">
          {partyName(record.recipient)}
        </span>
      ),
    },
    {
      key: 'origin',
      title: 'Origin',
      render: (_v, record) => (
        <span
          className="block max-w-[240px] truncate text-text-sub-600"
          title={record.quote?.origin?.address}
        >
          {record.quote?.origin?.address || '—'}
        </span>
      ),
    },
    {
      key: 'destination',
      title: 'Destination',
      render: (_v, record) => (
        <span
          className="block max-w-[240px] truncate text-text-sub-600"
          title={record.quote?.destination?.address}
        >
          {record.quote?.destination?.address || '—'}
        </span>
      ),
    },
    {
      key: 'pickup_sla_status',
      title: 'Pick Up SLA',
      render: (_v, record) => slaBadge(record.pickup_sla_status),
    },
    {
      key: 'complete_sla_status',
      title: 'Delivery SLA',
      render: (_v, record) => slaBadge(record.complete_sla_status),
    },
    {
      key: 'actions',
      title: '',
      render: (_v, record) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setDetailId(record.deliveryID);
          }}
          className="text-text-soft-400 hover:text-primary"
          aria-label="View detail"
        >
          <i className="ri-eye-line text-base" />
        </button>
      ),
    },
  ];

  return (
    <div className="p-4 space-y-5 sm:p-6">
      {/* Page head */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-bold text-primary">02</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-soft-400">
              Deliveries
            </span>
          </div>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-text-strong-950 sm:text-3xl">
            Deliveries
          </h1>
          <p className="max-w-2xl text-sm text-text-sub-600">
            Setiap pengiriman milik Anda di seluruh outlet — lengkap dengan
            order ID, jadwal, status, driver, dan SLA. Klik baris untuk membuka
            detail.
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap gap-3">
          <Button variant="secondary" onClick={handleRefresh}>
            <i className="ri-refresh-line mr-1.5" />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={handleDownloadCsv}
            disabled={downloadingCsv}
          >
            <i
              className={`mr-1.5 ${downloadingCsv ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'}`}
            />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Status tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {TILES.map((tile) => {
          const selected = activeTile === tile.key;
          return (
            <button
              key={tile.key}
              type="button"
              onClick={() => handleTileClick(tile.key)}
              className={`rounded-xl border bg-bg-white-0 p-4 text-left shadow-card-xs transition-all hover:border-stroke-sub-300 ${
                selected
                  ? 'border-primary ring-2 ring-feature-lighter'
                  : 'border-stroke-soft-200'
              }`}
            >
              <div className="mb-2.5 flex items-center gap-2 text-xs font-semibold text-text-sub-600">
                <span className={`h-2 w-2 rounded-full ${tile.dotClass}`} />
                {tile.label}
              </div>
              <div className="text-2xl font-bold tracking-tight text-text-strong-950">
                {tile.count(summary).toLocaleString()}
              </div>
            </button>
          );
        })}
      </div>

      {/* Table card — no `overflow-hidden`: it would clip the Filter popover
          when the table is short (e.g. empty results). */}
      <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke-soft-200 p-4">
          <InputSearch
            className="min-w-[200px] max-w-[420px] flex-1"
            placeholder="Search delivery ID, booking ID, rider…"
            onSearch={handleSearch}
            onClear={() => handleSearch('')}
          />
          <DeliveryFilter value={filters} onApply={handleApplyFilters} />
        </div>

        <TableData<Delivery>
          data={deliveries}
          columns={columns}
          loading={loading}
          className="rounded-none border-0 shadow-none"
          onRowClick={(record) => setDetailId(record.deliveryID)}
        />

        <div className="px-4 pb-4">
          <Pagination
            currentPage={page}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      </div>

      <DeliveryDetailDrawer
        deliveryID={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
};

export default DeliveriesPage;
